/**
 * Pattern-Bank fuer Scene 2 — "Talk to Your Cell".
 *
 * Demo-Theater: Statt einen echten Bedrock-Roundtrip im Call zu riskieren
 * (Latency, Auth, Rate-Limits), pattern-matchen wir gegen die typischen
 * Engineer-Fragen und liefern vorbereitete, generische Antworten zurueck.
 *
 * Fallback gibt's am Ende falls keine Pattern matcht — der LLM-Layer wuerde
 * hier in Production echt antworten.
 *
 * Bewusst KEINE realen Site-Codes — alles ueber Site Alpha aus
 * demo-constants.ts.
 */

export interface QaPattern {
  /** Regex die gegen die Voice-Transkription matched (case-insensitive). */
  match: RegExp;
  /** Label das ein User in der Hint-Liste sieht (sample-question). */
  hint: string;
  /** Antwort die AMZEL spricht. */
  answer: string;
}

/**
 * 8 Pattern fuer die wahrscheinlichsten Engineer-Fragen im Demo-Call.
 * Erste Treffer-Antwort gewinnt — Reihenfolge ist Prioritaet.
 */
export const SCENE2_PATTERNS: ReadonlyArray<QaPattern> = [
  {
    match: /\b(how many|count|number).*(repeater|repeaters)\b/i,
    hint: "How many repeaters are online?",
    answer:
      "Three of four repeaters are online at Site Alpha. RPT-03 has been offline since this morning — a replacement repeater is in transit. Coverage is currently maintained at ninety-six percent through the remaining three units in the IPSC cluster.",
  },
  {
    match: /\b(repeater|RPT)[-\s]*(0?3|three).*(offline|down|status|problem|why)\b/i,
    hint: "Why is repeater 03 offline?",
    answer:
      "Repeater RPT-03 went into thermal shutdown. The PA temperature exceeded ninety degrees Celsius for forty-five minutes before the protective cutoff engaged. Field IT was alerted, replacement unit is en route, estimated arrival in one hundred and twenty minutes.",
  },
  {
    match: /\b(battery|akku|akkus).*(health|status|gesundheit)\b/i,
    hint: "Show me battery health",
    answer:
      "Across the fleet at Site Alpha, average battery health is eighty-seven percent. Six hundred and twelve batteries in circulation, distribution spans sixty-two to ninety-nine percent capacity. Twenty-three batteries have crossed the seventy-five percent replacement threshold and are flagged for the next maintenance window.",
  },
  {
    match: /\b(rma|repair|reparatur|defekt|broken)\b/i,
    hint: "How many open RMAs?",
    answer:
      "Four open RMAs at Site Alpha. The earliest device is expected back in three days from the Berlin workshop. Two display defects, one battery issue, one water damage with microphone failure. All four under active warranty.",
  },
  {
    match: /\b(frequency|frequenz).*(licen[sc]e|expire|ablauf|valid)\b/i,
    hint: "When does the frequency license expire?",
    answer:
      "The earliest frequency licence at Site Alpha expires in twenty-two months. BNetzA reference active, annual fee covered by the customer. The system will warn you ninety days before any licence reaches expiration.",
  },
  {
    match: /\b(zone|area|aisle|pick|pack|inbound|outbound).*(active|busy|status|live|count|people|wer ist|how many)\b/i,
    hint: "What's happening in the Pick zone?",
    answer:
      "Pick zone is currently the busiest at Site Alpha. Channel load is at sixty-eight percent above the seven day baseline. Twelve active radio sessions in the last five minutes, voice stress markers are within normal range. No alerts.",
  },
  {
    match: /\b(stress|busy|overload|peak|hochbetrieb)\b/i,
    hint: "Is any team showing stress signals?",
    answer:
      "Aggregate voice stress is within normal range across all zones at Site Alpha. The Operations talkgroup shows slightly elevated speech rate in the last twenty minutes, but well within historical patterns. No intervention recommended.",
  },
  {
    match: /\b(coverage|survey|heatmap|signal)\b/i,
    hint: "When was the last coverage survey?",
    answer:
      "The most recent coverage heat-map for Site Alpha was completed nine months ago. All zones above the minus ninety-five dBm threshold. The next scheduled survey is in eight weeks. You can drill into the per-zone heat-map from the dossier page.",
  },
];

/**
 * Fallback wenn nichts matched — klingt natuerlich, hilft Engineer einen
 * sample-question zu finden ohne die Demo zu sprengen.
 */
export const SCENE2_FALLBACK =
  "I don't have data on that specifically — but I can show you the repeater status, battery health, the open RMA queue, channel load by zone, or the latest coverage survey. Which would you like?";

/**
 * Findet die erste matchende Pattern fuer eine Voice-Transkription.
 * Gibt die Antwort zurueck, oder den Fallback wenn nichts matched.
 */
export function answerForTranscript(transcript: string): string {
  for (const p of SCENE2_PATTERNS) {
    if (p.match.test(transcript)) return p.answer;
  }
  return SCENE2_FALLBACK;
}
